import { useEffect, useState } from 'react'
import { CheckCircle2, ExternalLink } from 'lucide-react'
import type { VtexConnection, VtexProbeResult } from '@/types'
import { useVendorAuth } from '@/auth/VendorAuthProvider'
import { PasswordInput, Spinner } from '@/components/ui'
import { api } from '@/lib/api'
import { RequestError } from '@/lib/api'
import { AdminSection } from './AdminSection'

/**
 * Conexión con VTEX (§Fase 2). El retailer carga acá sus propias credenciales: nunca se
 * las pedimos por fuera. Son write-only — el GET dice si están configuradas, nunca las
 * devuelve. Esta misma pantalla sirve para rotarlas cuando VTEX rote el token.
 */
export function AdminConnection() {
  const { refresh } = useVendorAuth()
  const [current, setCurrent] = useState<VtexConnection | null>(null)
  const [account, setAccount] = useState('')
  const [storeUrl, setStoreUrl] = useState('')
  const [appKey, setAppKey] = useState('')
  const [appToken, setAppToken] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [probe, setProbe] = useState<VtexProbeResult | null>(null)

  useEffect(() => {
    api
      .get<VtexConnection>('/admin/vtex-credentials')
      .then((c) => {
        setCurrent(c)
        setAccount(c.account)
        setStoreUrl(c.storeUrl)
      })
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false))
  }, [])

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setSaved(false)
    setErr(null)
    setProbe(null)
    try {
      const res = await api.put<VtexConnection>('/admin/vtex-credentials', {
        account: account.trim(),
        appKey: appKey.trim(),
        appToken: appToken.trim(),
        storeUrl: storeUrl.trim(),
      })
      setCurrent(res)
      setAppKey('')
      setAppToken('')
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
      // Refleja el nuevo tenantStatus ('active') en el contexto: sin esto, el back arrow y
      // el bottom-nav siguen leyendo 'provisioning' y rebotan al admin de vuelta acá (A1).
      await refresh()
    } catch (e) {
      setErr((e as Error).message)
      // El 422 del probe trae el detalle por recurso.
      const body = (e as RequestError & { probe?: VtexProbeResult }).probe
      if (body) setProbe(body)
    } finally {
      setSaving(false)
    }
  }

  const row = (label: string, value?: string) => (
    <div className="flex items-center justify-between text-xs">
      <span className="text-ink-muted">{label}</span>
      <span className={value === 'ok' ? 'text-success' : 'text-danger'}>{value === 'ok' ? 'OK' : value}</span>
    </div>
  )

  if (loading) {
    return (
      <AdminSection title="Conexión con VTEX">
        <div className="flex justify-center py-10">
          <Spinner className="h-6 w-6 accent-text" />
        </div>
      </AdminSection>
    )
  }

  return (
    <AdminSection title="Conexión con VTEX">
      <form onSubmit={save} className="space-y-5">
        {current?.configured ? (
          <div className="flex items-center gap-2 rounded-lg bg-success/5 px-3 py-2 text-sm text-success">
            <CheckCircle2 className="h-4 w-4" />
            Conexión activa con la cuenta <strong>{current.account}</strong>
          </div>
        ) : (
          <div className="rounded-lg bg-warning/5 px-3 py-2 text-sm text-ink">
            Para que la app funcione, cargá las credenciales de tu cuenta VTEX. Tus vendedores no
            pueden ingresar hasta que la conexión esté activa.
          </div>
        )}

        <div>
          <label className="field-label">Cuenta VTEX (accountName)</label>
          <input className="field-input" value={account} onChange={(e) => setAccount(e.target.value)} placeholder="mitienda" />
        </div>

        <div>
          <label className="field-label">URL de la tienda</label>
          <input className="field-input" value={storeUrl} onChange={(e) => setStoreUrl(e.target.value)} placeholder="https://www.mitienda.com" />
        </div>

        <div>
          <label className="field-label">App Key</label>
          <input className="field-input font-mono text-xs" value={appKey} onChange={(e) => setAppKey(e.target.value)} placeholder="vtexappkey-mitienda-XXXXXX" autoComplete="off" />
        </div>

        <div>
          <label className="field-label">App Token</label>
          <PasswordInput className="font-mono text-xs" value={appToken} onChange={(e) => setAppToken(e.target.value)} placeholder="••••••••" autoComplete="off" />
          <p className="mt-1 text-xs text-ink-muted">
            Por seguridad no mostramos las credenciales guardadas. Para cambiarlas, pegá las nuevas.
          </p>
        </div>

        <div className="rounded-xl border border-line p-3 text-xs text-ink-muted">
          <p className="mb-1 font-semibold text-ink">Roles que necesita la App Key</p>
          <p>Catalog (read) · OMS (ver pedidos) · Master Data v2 (read)</p>
          <a className="mt-2 inline-flex items-center gap-1 accent-text" href="https://help.vtex.com/es/tutorial/application-keys--2iffYzlvvz4BDMr6WGUtet" target="_blank" rel="noreferrer">
            Cómo crear una App Key en VTEX <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {err && (
          <div className="space-y-2 rounded-lg bg-danger/5 px-3 py-2 text-sm text-danger">
            <p>{err}</p>
            {probe && (
              <div className="space-y-1 border-t border-danger/20 pt-2">
                {row('Catalog', probe.catalog)}
                {row('OMS', probe.oms)}
                {row('Master Data', probe.masterdata)}
              </div>
            )}
          </div>
        )}

        <button type="submit" className="btn-primary btn-block" disabled={saving || !account || !storeUrl || !appKey || !appToken}>
          {saving ? <Spinner className="h-4 w-4" /> : saved ? '✅ Conexión verificada y guardada' : 'Verificar y guardar'}
        </button>
      </form>
    </AdminSection>
  )
}
